import React from 'react';
import { makeStyles } from '@material-ui/core';
import { PaymentCardOption } from '../../../api/paymentCards';
import { FORM_COLORS, HIGHLIGHT_OPTIONS } from '../constants';
import { useFormStyles } from '../styles';
import { PaymentMethodsTabProps } from '../types';
import { LabeledField, SelectInput } from '../components/FormControls';
import { ZoneMultiSelect } from '../../PublicationsHeader/components/ZoneMultiSelect';
import { InfoBox } from '../components/InfoBox';

const useStyles = makeStyles(() => ({
  readonlyBox: {
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 18,
  },
  readonlyHead: {
    background: FORM_COLORS.bg,
    padding: '8px 12px',
    fontSize: 11,
    fontWeight: 500,
    color: FORM_COLORS.textSecondary,
    borderBottom: `0.5px solid ${FORM_COLORS.border}`,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
  },
  readonlyRow: {
    padding: '10px 12px',
    borderBottom: `0.5px solid ${FORM_COLORS.border}`,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  cards: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    flex: 1,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 7px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 500,
    border: '0.5px solid transparent',
    background: FORM_COLORS.greenBg,
    color: FORM_COLORS.green,
    borderColor: FORM_COLORS.greenBorder,
  },
  empty: {
    fontSize: 12,
    color: FORM_COLORS.textSecondary,
    padding: '4px 0',
  },
}));

export const PaymentMethodsTab = ({
  values,
  onChange,
  targetOptions,
  paymentCardOptions,
  loading,
  loadingPaymentCards,
}: PaymentMethodsTabProps) => {
  const classes = useStyles();
  const formClasses = useFormStyles();

  return (
    <div>
      <InfoBox tone="blue">
        Las formas de pago se derivan automáticamente de las campañas seleccionadas. Para
        modificarlas, editá la campaña desde el módulo de Gestión Comercial.
      </InfoBox>

      <div className={classes.readonlyBox}>
        <div className={classes.readonlyHead}>
          Formas de pago activas para esta publicación
        </div>
        {values.campaigns.length === 0 ? (
          <div className={classes.readonlyRow}>
            <span className={classes.empty}>Sin campañas asignadas.</span>
          </div>
        ) : loading ? (
          <div className={classes.readonlyRow}>
            <span className={classes.empty}>Cargando formas de pago...</span>
          </div>
        ) : targetOptions.length === 0 ? (
          <div className={classes.readonlyRow}>
            <span className={classes.empty}>
              No hay formas de pago asociadas a las campañas seleccionadas.
            </span>
          </div>
        ) : (
          <div className={classes.readonlyRow}>
            <div className={classes.cards}>
              {targetOptions.map((target) => (
                <span key={target.id} className={classes.badge}>
                  {target.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className={formClasses.sectionTitle}>Método de destacados</p>
      <LabeledField label="Destacados">
        <SelectInput
          value={values.highlightMethod}
          onChange={(value) => onChange('highlightMethod', value)}
          options={HIGHLIGHT_OPTIONS}
          style={{ maxWidth: 280 }}
        />
      </LabeledField>

      <p className={formClasses.sectionTitle} style={{ marginTop: 16 }}>
        Un pago — tarjetas habilitadas
      </p>
      <LabeledField
        label="Seleccioná las tarjetas para 1 pago"
        required
        hint="Seleccioná las tarjetas habilitadas para el precio de 1 pago en este producto."
      >
        <ZoneMultiSelect
          options={paymentCardOptions}
          selected={values.oneePaymentCards}
          onChange={(next) => onChange('oneePaymentCards', next)}
          placeholder="Seleccioná las tarjetas..."
          loading={loadingPaymentCards ?? loading}
        />
      </LabeledField>
    </div>
  );
};
